import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, Search } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "@/lib/api";
import ReadingStats from "@/components/ReadingStats";
import LibraryTabs from "@/components/LibraryTabs";
import { useReading } from "@/contexts/ReadingContext";

function coverFor(book) {
    if (book.isbn) {
        return `https://covers.openlibrary.org/b/isbn/${book.isbn}-L.jpg?default=false`;
    }
    return null;
}

export default function Leidos() {
    const { t } = useTranslation();
    const [items, setItems] = useState(null);
    const [filter, setFilter] = useState("");
    const { toggleRead } = useReading();

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const { data } = await api.get("/read/list");
                if (alive) setItems(data);
            } catch (e) {
                toast.error(formatApiError(e.response?.data?.detail));
                if (alive) setItems([]);
            }
        })();
        return () => { alive = false; };
    }, []);

    const filtered = (items || []).filter((b) => {
        const q = filter.trim().toLowerCase();
        if (!q) return true;
        return (
            (b.title || "").toLowerCase().includes(q) ||
            (b.author || "").toLowerCase().includes(q)
        );
    });

    if (items === null) {
        return (
            <main className="min-h-[calc(100vh-73px)] flex items-center justify-center">
                <p className="font-display text-2xl text-[var(--ink)] animate-slow-pulse italic">
                    {t("leidos.loading")}
                </p>
            </main>
        );
    }

    return (
        <main className="min-h-[calc(100vh-73px)] max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-16 md:py-24">
            <div className="border-b border-[var(--border)] pb-10 mb-12">
                <p className="text-xs uppercase tracking-[0.3em] text-[var(--accent)] mb-3">
                    {t("leidos.eyebrow")}
                </p>
                <h1
                    className="font-display text-5xl md:text-7xl text-[var(--ink)] tracking-tight"
                    style={{ fontVariationSettings: '"wght" 700, "wdth" 75, "opsz" 144' }}
                >
                    {t("leidos.title")}
                </h1>
                <p className="text-[var(--ink-muted)] mt-4 text-lg max-w-2xl">
                    {t("leidos.lead")}
                </p>
            </div>

            <LibraryTabs />

            <div className="mb-12">
                <ReadingStats />
            </div>

            {items.length === 0 ? (
                <div
                    data-testid="empty-leidos-state"
                    className="text-center py-24 border border-[var(--border-strong)] bg-[var(--bg-elevated)]"
                >
                    <BookOpen size={36} strokeWidth={1} className="mx-auto text-[var(--accent)] mb-4" />
                    <p
                        className="font-display text-3xl text-[var(--ink)] mb-3"
                        style={{ fontVariationSettings: '"wght" 600, "wdth" 90, "opsz" 64' }}
                    >
                        {t("leidos.empty_title")}
                    </p>
                    <p className="text-[var(--ink-muted)] mb-8 max-w-md mx-auto">
                        {t("leidos.empty_lead")}
                    </p>
                    <Link
                        to="/"
                        data-testid="empty-leidos-cta"
                        className="inline-block bg-[var(--ink)] text-[var(--bg)] rounded-full hover:bg-[var(--accent)] transition-colors px-8 py-3 text-sm uppercase tracking-widest"
                    >
                        {t("leidos.empty_cta")}
                    </Link>
                </div>
            ) : (
                <>
                    <div className="flex items-center gap-3 border-b border-[var(--border-strong)] pb-3 mb-10 max-w-md">
                        <Search size={16} strokeWidth={1.5} className="text-[var(--ink-dim)]" />
                        <input
                            data-testid="leidos-filter-input"
                            type="text"
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            placeholder={t("leidos.filter_placeholder")}
                            className="flex-1 bg-transparent border-0 outline-none text-base text-[var(--ink)] py-1"
                        />
                        <span className="text-xs text-[var(--ink-dim)] font-mono-num whitespace-nowrap">
                            {t("leidos.filter_count", { filtered: filtered.length, total: items.length })}
                        </span>
                    </div>

                    <div
                        data-testid="leidos-grid"
                        className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-6"
                    >
                        {filtered.map((book, i) => (
                            <ReadCard
                                key={book.book_key}
                                book={book}
                                index={i}
                                removeLabel={t("leidos.remove")}
                                onUnread={async () => {
                                    await toggleRead(book);
                                    setItems((prev) =>
                                        prev.filter((b) => b.book_key !== book.book_key)
                                    );
                                }}
                            />
                        ))}
                    </div>
                </>
            )}
        </main>
    );
}

function ReadCard({ book, index, onUnread, removeLabel }) {
    const [imgError, setImgError] = useState(false);
    const url = coverFor(book);
    return (
        <article
            data-testid={`read-card-${index}`}
            className="group relative flex flex-col animate-fade-up"
            style={{ animationDelay: `${index * 35}ms` }}
        >
            <div className="aspect-[2/3] bg-[var(--bg-strong)] border border-[var(--border)] overflow-hidden flex items-center justify-center transition-transform duration-300 group-hover:-translate-y-1">
                {url && !imgError ? (
                    <img
                        src={url}
                        alt={book.title}
                        onError={() => setImgError(true)}
                        loading="lazy"
                        className="w-full h-full object-cover"
                    />
                ) : (
                    <div className="flex flex-col items-center justify-center text-center px-3 py-4">
                        <BookOpen size={20} strokeWidth={1} className="text-[var(--accent)] mb-2" />
                        <span
                            className="font-display text-sm text-[var(--ink)] leading-tight line-clamp-3"
                            style={{ fontVariationSettings: '"wght" 600, "wdth" 95, "opsz" 18' }}
                        >
                            {book.title}
                        </span>
                    </div>
                )}
                <button
                    type="button"
                    data-testid={`unread-btn-${index}`}
                    onClick={onUnread}
                    aria-label={removeLabel}
                    className="absolute top-2 right-2 bg-[var(--ink)]/90 text-[var(--bg)] rounded-full opacity-0 group-hover:opacity-100 transition-opacity px-2.5 py-1 text-[10px] uppercase tracking-widest hover:bg-[var(--danger)] hover:text-[var(--ink)]"
                >
                    {removeLabel}
                </button>
            </div>
            <p
                className="font-display text-base text-[var(--ink)] mt-3 leading-tight line-clamp-2"
                style={{ fontVariationSettings: '"wght" 500, "wdth" 95, "opsz" 18' }}
            >
                {book.title}
            </p>
            <p className="text-xs text-[var(--ink-dim)] mt-1 line-clamp-1">{book.author}</p>
        </article>
    );
}
